import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

/** Value of the single `AccordionItem` every collapsible section renders. */
const OPEN_VALUE = 'content';

interface AccordionOpenStateProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
}

interface CollapsibleSectionsContextValue {
  isOpen: (id: string) => boolean;
  setOpen: (id: string, open: boolean) => void;
  expandAll: () => void;
  collapseAll: () => void;
}

const CollapsibleSectionsContext = createContext<CollapsibleSectionsContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
  defaultOpen?: boolean;
}

/**
 * Coordinates a group of collapsible sections so a single control can expand or collapse all of them.
 *
 * State is a shared fallback plus per-section overrides rather than one entry per section, so nothing has to
 * register itself — and expand/collapse all clears the overrides, which means pressing the same control twice
 * in a row still re-syncs sections the user toggled in between.
 */
export const CollapsibleSectionsProvider = ({ children, defaultOpen = true }: ProviderProps): ReactElement => {
  const [fallbackOpen, setFallbackOpen] = useState(defaultOpen);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const expandAll = useCallback(() => {
    setOverrides({});
    setFallbackOpen(true);
  }, []);

  const collapseAll = useCallback(() => {
    setOverrides({});
    setFallbackOpen(false);
  }, []);

  const value = useMemo<CollapsibleSectionsContextValue>(
    () => ({
      isOpen: (id) => overrides[id] ?? fallbackOpen,
      setOpen: (id, open) => setOverrides((prev) => ({ ...prev, [id]: open })),
      expandAll,
      collapseAll,
    }),
    [collapseAll, expandAll, fallbackOpen, overrides]
  );

  return <CollapsibleSectionsContext.Provider value={value}>{children}</CollapsibleSectionsContext.Provider>;
};

/** Controls for the surrounding provider. Returns null when rendered outside one. */
export const useCollapsibleSectionsControls = (): Pick<
  CollapsibleSectionsContextValue,
  'expandAll' | 'collapseAll'
> | null => {
  const context = useContext(CollapsibleSectionsContext);
  if (!context) return null;
  return { expandAll: context.expandAll, collapseAll: context.collapseAll };
};

/**
 * Props to spread on a section's `<Accordion type="single" collapsible>`.
 *
 * Without a provider the accordion stays uncontrolled and open by default, so sections keep working
 * (and stay testable) on their own.
 */
export const useCollapsibleSection = (id: string): AccordionOpenStateProps => {
  const context = useContext(CollapsibleSectionsContext);

  if (!context) return { defaultValue: OPEN_VALUE };

  return {
    value: context.isOpen(id) ? OPEN_VALUE : '',
    onValueChange: (next) => context.setOpen(id, next === OPEN_VALUE),
  };
};
